
import React, { useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Presentation, BookOpen, MapPin, LineChart, BrainCircuit, MessageCircle } from 'lucide-react';
import PageHero from '@/components/ui/page-hero';
import PageSection from '@/components/ui/page-section';
import SectionHeader from '@/components/ui/section-header';
import ContactDialog from '../components/ContactDialog';

const AIConsulting: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      icon: <BrainCircuit className="h-12 w-12 text-primary" />,
      title: "AI Readiness Assessment",
      description: "We evaluate your organization's potential for AI adoption and identify high-impact opportunities.",
      features: [
        "Comprehensive analysis of business processes",
        "Data readiness evaluation",
        "Identification of AI implementation opportunities",
        "ROI projections for AI initiatives"
      ]
    },
    {
      icon: <Presentation className="h-12 w-12 text-primary" />,
      title: "AI Education & Training",
      description: "Customized workshops and training programs to build AI literacy across your organization.",
      features: [
        "Executive AI literacy programs",
        "Technical team AI training",
        "Department-specific AI workshops",
        "Ongoing AI educational resources"
      ]
    },
    {
      icon: <MapPin className="h-12 w-12 text-primary" />,
      title: "AI Adoption Roadmaps",
      description: "Strategic planning to guide your organization's AI transformation journey.",
      features: [
        "Phased implementation strategy",
        "Technology stack recommendations",
        "Change management planning",
        "AI governance framework"
      ]
    },
    {
      icon: <LineChart className="h-12 w-12 text-primary" />,
      title: "AI Solution Design",
      description: "End-to-end design of custom AI solutions aligned with your business objectives.",
      features: [
        "Custom AI solution architecture",
        "Proof of concept development",
        "Data strategy planning",
        "Ethical AI implementation"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <PageHero 
        badge="Expert Services"
        title="AI Strategy & Corporate Training"
        description="Partner with our AI experts to transform your business capabilities through strategic AI adoption, education, and implementation planning."
        buttons={[
          {
            text: "Book a Discovery Call", // Changed from "Book a Consulting Session"
            variant: "blue",
            component: (
              <ContactDialog 
                trigger={
                  <Button variant="blue" size="lg">
                    Book a Discovery Call
                  </Button>
                }
                title="Book a Discovery Call"
                description="Fill out the form below to schedule a consultation with our AI specialists."
              />
            )
          },
          {
            text: "View Case Studies",
            variant: "outline",
            href: "/portfolio"
          }
        ]}
      />

      <PageSection>
        <div className="flex flex-col md:flex-row gap-12 items-center">
          <div className="md:w-1/2">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-primary/20 text-primary mb-4">
              <span className="text-sm font-medium">Our Approach</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Why Partner With Us For AI Consulting</h2>
            <p className="text-lg text-gray-700 mb-8">
              Our consultants bring practical AI expertise to the table, helping you navigate the complex landscape of artificial intelligence with confidence and clarity.
            </p>
            
            <ul className="space-y-4">
              {[
                "AI specialists with real-world implementation experience",
                "Practical, business-first approach to AI adoption",
                "Custom strategies tailored to your industry and challenges",
                "Focus on measurable results and ROI",
                "Ongoing support throughout your AI transformation journey"
              ].map((point, index) => (
                <li key={index} className="flex items-start">
                  <span className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-3">
                    <svg className="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  <span className="text-gray-800">{point}</span>
                </li>
              ))}
            </ul>
          </div>
          
          <div className="md:w-1/2">
            <div className="bg-blue-50 p-8 rounded-xl border border-blue-100">
              <div className="rounded-full bg-blue-100 p-4 inline-flex mb-6">
                <MessageCircle className="h-10 w-10 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Our Consulting Philosophy</h3>
              <p className="text-gray-700 mb-6">
                We believe AI should be accessible, understandable, and practical for businesses of all sizes. Our goal is to demystify AI and help you implement solutions that deliver real business value without unnecessary complexity.
              </p>
            </div>
          </div>
        </div>
      </PageSection>

      <PageSection dark>
        <SectionHeader 
          title="Our AI Consulting Services"
          description="Comprehensive services to help your organization harness the power of artificial intelligence."
          descriptionClassName="text-white/80"
        />
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <Card key={index} className="border border-gray-700 bg-gray-800/60 glass-effect overflow-hidden">
              <CardContent className="p-8">
                <div className="mb-6 p-4 rounded-full bg-primary/10 inline-block">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-bold mb-4 text-white">
                  {service.title}
                </h3>
                <p className="text-white/80 mb-6">
                  {service.description}
                </p>
                
                <h4 className="font-semibold mb-3 text-white">Key Components:</h4>
                <ul className="space-y-2 text-white/80">
                  {service.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start">
                      <span className="mr-2 text-primary">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      </PageSection>
      
      <PageSection>
        <SectionHeader 
          title="Our Consulting Process"
          description="A structured approach to help your organization adopt AI successfully."
        />
        
        <div className="relative">
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-primary/20 hidden md:block"></div>
          
          <div className="space-y-12 relative">
            {[
              {
                step: "Discovery",
                description: "We begin with a thorough assessment of your business, identifying challenges, opportunities, and strategic goals that AI can help address."
              },
              {
                step: "Strategy Development",
                description: "Based on our findings, we develop a customized AI strategy including recommended solutions, implementation roadmap, and resource requirements."
              },
              {
                step: "Knowledge Transfer",
                description: "We conduct workshops and training sessions to build AI literacy and capabilities within your team."
              },
              {
                step: "Implementation Planning",
                description: "We create detailed plans for implementing AI solutions, including technology selection, data requirements, and integration considerations."
              },
              {
                step: "Ongoing Support",
                description: "We provide continued guidance and support as you implement your AI strategy, ensuring successful adoption and maximum value."
              }
            ].map((phase, index) => (
              <div key={index} className="flex items-start relative">
                <div className="flex-shrink-0 z-10">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white font-bold">
                    {index + 1}
                  </div>
                </div>
                <div className="ml-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{phase.step}</h3>
                  <p className="text-gray-700">{phase.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </PageSection>
      
      <PageSection dark>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Transform Your Business with AI?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Schedule a consultation with our AI experts to discuss how we can help your organization harness the power of artificial intelligence.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <ContactDialog 
              trigger={
                <Button size="lg" className="btn-hover-effect group">
                  Book a Consulting Session
                </Button>
              }
              title="Book a Consulting Session"
              description="Fill out the form below to schedule a consultation with our AI specialists."
            />
            <ContactDialog 
              trigger={
                <Button variant="outline" size="lg" className="bg-transparent border-white/20 text-white hover:bg-white/10">
                  Contact Us
                </Button>
              }
              type="contact"
            />
          </div>
        </div>
      </PageSection>

      <Footer />
    </div>
  );
};

export default AIConsulting;

